<script lang="ts">
export default {
    name: 'BaseButton',
};
</script>

<script lang="ts" setup>
import Loading from '@pet/base.loading/index.vue';
import { debounce as effectDebounce } from '@pet/base.utils/index';
import { computed } from 'vue-demi';

import type { ButtonType, ButtonSize } from './types';

interface ButtonProps {
    /**
     * 按钮类型
     */
    type?: ButtonType;
    /**
     * 禁用状态
     */
    disabled?: boolean;
    /**
     * 禁止态，但可以点击
     */
    looksLikeDisabled?: boolean;
    /**
     * 加载态
     */
    loading?: boolean;
    /**
     * 动效开关
     */
    animation?: boolean;
    /**
     * 按钮尺寸
     */
    size?: ButtonSize;
    /**
     * 防抖时间
     */
    debounce?: number;
    /**
     * 呼吸动效
     */
    breathing?: boolean;
    /**
     * 边框
     */
    border?: boolean;
    /**
     * 彩色loading
     */
    colorfulLoading?: boolean;
}

const props = withDefaults(defineProps<ButtonProps>(), {
    type: 'primary',
    disabled: false,
    looksLikeDisabled: false,
    loading: false,
    animation: false,
    size: 'l',
    debounce: 200,
});

const colorfulLoading = computed(() => props.type === 'plain' || props.colorfulLoading);

const btnClasses = computed(() => {
    return ['btn-main-box', `btn-${props.type}`];
});

const btnStatus = computed(() => {
    return [
        `btn-size-${props.size}`,
        props.border === true && 'btn-border',
        (props.disabled || props.looksLikeDisabled) && 'btn-disabled',
        props.loading && 'btn-loading',
        props.animation && 'btn-animation',
        props.breathing === true && 'btn-breathing',
    ];
});

const emit = defineEmits<{
    (e: 'click', params: Event): void;
}>();

function clickHandle(e: Event) {
    if (props.disabled || props.loading) {
        e.preventDefault();
    } else {
        emit('click', e);
    }
}

const debounceClick = effectDebounce(clickHandle, props.debounce);
</script>

<template>
    <span role="button" class="btn-wrapper" :class="btnStatus" @click="debounceClick">
        <span :class="btnClasses">
            <span v-if="animation" class="btn-shine-box">
                <span class="btn-shine"></span>
            </span>
            <span v-if="$slots.sup">
                <slot name="sup" />
            </span>
            <Loading v-if="loading" type="circle" :colorful="colorfulLoading" class="loading" />
            <span v-else class="btn-words">
                <span v-if="$slots.icon" class="btn-icon">
                    <slot name="icon" />
                </span>
                <slot />
                <span v-if="$slots.plugin" class="btn-words-plugin">
                    <slot name="plugin"></slot>
                </span>
            </span>
        </span>
    </span>
</template>

<style lang="scss" scoped>
@use 'sass:map';

@function map-deep-get($map, $keys...) {
    @each $key in $keys {
        $map: map-get($map, $key);
    }
    @return $map;
}

$button-red-color: #fe3666;
$button-bg-cny2023: linear-gradient(275.16deg, #fe3666 1.91%, #fe3666 48.66%, #ff7d49 118.64%);

$btn-style: (
    primary: (
        color: var(--base-btn-font-color, #fff),
        background: var(--base-btn-background, $button-bg-cny2023),
    ),
    secondary: (
        color: var(--base-btn-secondary-font-color, #fff),
        background: var(--base-btn-secondary-background, $button-red-color),
    ),
);

$plain-border: 1px;
$border-outline: var(--base-btn-out-line, linear-gradient(to bottom, #ffe9cd 0%, #ffd2a8 100%));

$btn-sets: (
    l: (
        w: 192px,
        h: 56px,
        font-size: 17px,
        padding: 24px,
        outline: 2px,
    ),
    lm: (
        w: 128px,
        h: 48px,
        font-size: 14px,
        padding: 16px,
        outline: 2px,
    ),
    m: (
        w: 82px,
        h: 40px,
        font-size: 14px,
        padding: 12px,
        outline: 1.5px,
    ),
    s: (
        w: 82px,
        h: 32px,
        font-size: 13px,
        padding: 12px,
        outline: 1.5px,
    ),
    xs: (
        w: 60px,
        h: 24px,
        font-size: 12px,
        padding: 9px,
        outline: 1px,
    ),
    xxs: (
        w: 48px,
        h: 16px,
        font-size: 10px,
        padding: 4px,
        outline: 1px,
    ),
);

.btn-wrapper {
    color: $button-red-color;
    cursor: pointer;
    display: inline-flex;
    border-radius: 100px;
    transition: transform 150ms ease-in-out;
    font-weight: 700;
    &:active:not(.btn-disabled):not(.btn-loading) {
        transform: scale(0.95);
        opacity: 0.5;
        :not(.btn-plain) .btn-words {
            opacity: 0.5;
        }
    }
}

.btn-main-box {
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    -webkit-user-select: none;
    box-sizing: border-box;
    .loading {
        font-size: calc(54em / 32);
    }
}

.btn-disabled {
    opacity: 0.5;
    .btn-shine {
        display: none;
    }
    :not(.btn-plain) {
        .btn-words {
            opacity: 0.5;
        }
    }
}

.btn-animation {
    .btn-main-box {
        position: relative;
    }
}
.btn-border {
    display: inline-flex;
    position: relative;
    .btn-main-box {
        position: relative;
    }
    &::before {
        content: '';
        position: absolute;
        background: $border-outline;
        border-radius: inherit;
    }
}

@each $size, $info in $btn-sets {
    .btn-size-#{$size} {
        font-size: map.get($info, 'font-size');
        min-width: map.get($info, 'w');
        height: map.get($info, 'h');
        &::before {
            top: 0 - map.get($info, 'outline');
            right: 0 - map.get($info, 'outline');
            bottom: 0 - map.get($info, 'outline');
            left: 0 - map.get($info, 'outline');
        }

        .btn-main-box {
            border-radius: map.get($info, 'h');
            padding: 0 map.get($info, 'padding');
        }
    }
}

.btn-words {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-wrap: wrap;
}

.btn-icon {
    width: 1em * calc(28 / 17);
    height: 1em * calc(28 / 17);
    margin-right: 1em * calc(4 / 17);
    :deep(img),
    :deep(svg) {
        width: 100%;
        height: 100%;
    }
}

.btn-words-plugin {
    display: block;
    width: 100%;
    font-size: 12px;
    line-height: 17px;
    font-weight: 400;
}

.btn-primary {
    color: map-deep-get($btn-style, 'primary', 'color');
    background: map-deep-get($btn-style, 'primary', 'background');
}

.btn-secondary {
    color: map-deep-get($btn-style, 'secondary', 'color');
    background: map-deep-get($btn-style, 'secondary', 'background');
}

.btn-plain {
    color: inherit;
    font-weight: 700;
    border: $plain-border solid;
    border-radius: 50px;
    box-sizing: border-box;
}

.btn-shine-box {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    overflow: hidden;
    border-radius: 50px;
    transform: translate(0);
    pointer-events: none;
    -webkit-mask-image: -webkit-radial-gradient(#fff, #000);
}

.btn-shine {
    display: block;
    width: 100%;
    height: 100%;
    background-image: url('./assets/shine.png');
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    animation: btn-sharp 2668ms linear infinite;
    pointer-events: none;
}

.btn-breathing {
    animation: btn-breathing 1000ms cubic-bezier(0.33, 0, 0.67, 1) infinite;
}

@keyframes btn-sharp {
    0% {
        transform: translate(-100%, 0);
    }
    50% {
        transform: translate(200%, 0);
    }
    100% {
        transform: translate(200%, 0);
    }
}

@keyframes btn-breathing {
    0%,
    100% {
        transform: scale(0.96);
    }
    48% {
        transform: scale(1);
    }
}
</style>
